import type { SceneObject, TraceScene } from '../../exports/scene';
import { readMeshSource, type GeometrySource } from './mesh-source';

// Match the asset, not the object's name: renamed objects and clones keep their
// provenance; revising a prototype must not give old copies the new source code.
// Blob identity also prevents stale provenance after a scene reload or resource
// replacement reuses the same asset path. No code is recovered by filename alone.
const sources = new WeakMap<TraceScene, WeakMap<Blob, GeometrySource>>();

export function rememberGeometrySource(scene: TraceScene, object: SceneObject, source: GeometrySource): void {
    const asset = object.src ? scene.resources.getAsset(object.src) : undefined;
    if (!asset) return;
    let records = sources.get(scene);
    if (!records) { records = new WeakMap(); sources.set(scene, records); }
    records.set(asset, structuredClone(source));
}

/** Available authoring data for this exact immutable asset, never a filename match. */
export function geometrySource(scene: TraceScene, object: SceneObject): GeometrySource | undefined {
    const asset = object.src ? scene.resources.getAsset(object.src) : undefined;
    const source = asset ? sources.get(scene)?.get(asset) : undefined;
    return source ? structuredClone(source) : undefined;
}

// Cache misses by immutable Blob identity too; foreign assets need not be parsed
// on every call. Replacing an asset with new bytes requires a fresh inspection.
const embeddedSources = new WeakMap<Blob, Promise<GeometrySource | null>>();
export async function restoreGeometrySources(scene: TraceScene, objects: SceneObject[]): Promise<void> {
    for (const object of objects) {
        const asset = object.src ? scene.resources.getAsset(object.src) : undefined;
        if (!asset || sources.get(scene)?.has(asset)) continue;
        let pending = embeddedSources.get(asset);
        if (!pending) { pending = readMeshSource(asset); embeddedSources.set(asset, pending); }
        const source = await pending;
        if (source && object.src && scene.resources.getAsset(object.src) === asset) rememberGeometrySource(scene, object, source);
    }
}
